import {
  getFirestore,
  collection,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  getDocs,
  query,
  where,
  writeBatch,
  onSnapshot,
  orderBy,
} from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import { getStorage, ref, getDownloadURL } from 'firebase/storage';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { app } from '../../boots/firebase';

export const getOrganizations = ({ state, commit }) => {
  const db = getFirestore();

  commit('SET_STATE', { key: 'isLoading', value: true });

  const orgQuery = query(collection(db, 'organizations'), orderBy('name'));
  onSnapshot(orgQuery, async (snapshot) => {
    const organizations = [...state.organizations];
    const changes = snapshot.docChanges();

    // Process all changes in parallel but wait for all to complete
    const changePromises = changes.map(async (c) => {
      if (c.type === 'added') {
        const organization = { ...c.doc.data(), id: c.doc.id };
        try {
          const orgLogoPath = decodeURIComponent(organization.orgLogoLink);
          if (orgLogoPath) {
            const storage = getStorage();
            const logoRef = ref(storage, orgLogoPath);
            organization.orgLogoLink = await getDownloadURL(logoRef);
          } else {
            organization.orgLogoLink = null;
          }
        } catch (error) {
          organization.orgLogoLink = null;
        }
        return { type: 'add', organization };
      }
      if (c.type === 'modified') {
        const organizationIndex = organizations.findIndex((s) => s.id === c.doc.id);
        const organization = { ...c.doc.data(), id: c.doc.id };
        const existingOrg = organizations[organizationIndex];

        try {
          const orgLogoPath = decodeURIComponent(organization.orgLogoLink);
          // If logo path exists and is different from existing, or if we don't have an existing downloadable URL
          if (orgLogoPath && (!existingOrg?.orgLogoLink || !existingOrg.orgLogoLink.startsWith('https://'))) {
            const storage = getStorage();
            const logoRef = ref(storage, orgLogoPath);
            organization.orgLogoLink = await getDownloadURL(logoRef);
          } else if (orgLogoPath && existingOrg?.orgLogoLink && existingOrg.orgLogoLink.startsWith('https://')) {
            // Preserve existing downloadable URL if logo path hasn't changed
            organization.orgLogoLink = existingOrg.orgLogoLink;
          } else {
            organization.orgLogoLink = null;
          }
        } catch (error) {
          // If there's an error but we have an existing URL, preserve it
          organization.orgLogoLink = existingOrg?.orgLogoLink || null;
        }
        return { type: 'modify', index: organizationIndex, data: organization };
      }
      if (c.type === 'removed') {
        const organizationIndex = organizations.findIndex((s) => s.id === c.doc.id);
        return { type: 'remove', index: organizationIndex };
      }
      return null;
    });

    // Wait for all async operations to complete
    const processedChanges = await Promise.all(changePromises);

    // Apply all changes atomically
    processedChanges.forEach((change) => {
      if (!change) return;

      if (change.type === 'add') {
        organizations.push(change.organization);
      } else if (change.type === 'modify' && change.index !== -1) {
        organizations[change.index] = change.data;
      } else if (change.type === 'remove' && change.index !== -1) {
        organizations.splice(change.index, 1);
      }
    });

    commit('SET_STATE', { key: 'organizations', value: organizations });
    commit('SET_STATE', { key: 'isLoading', value: false });

    if (!state.isInitialized) commit('INITIALIZE');
  });
};

export const submitOrganization = async ({ commit }, { payload }) => {
  const db = getFirestore();
  const { organizationLogo, ...newPayload } = payload;
  commit('SET_STATE', { key: 'isLoading', value: true });
  try {
    let orgLogoLink = '';
    if (payload.orgLogoBase64) {
      const functions = getFunctions(app);
      const uploadFile = httpsCallable(functions, 'uploadFile');
      const uploadResp = await uploadFile({
        ...payload,
        name: payload.orgLogoName,
      });
      orgLogoLink = uploadResp.data.logoLink;
    }
    if (payload.id) {
      const orgRef = doc(db, 'organizations', payload.id);
      await updateDoc(orgRef, {
        ...newPayload,
        orgLogoLink,
      });
    } else {
      await addDoc(collection(db, 'organizations'), {
        ...newPayload,
        orgLogoLink,
      });
    }
  } catch (error) {
    commit('SET_STATE', { key: 'error', value: error.message });
  } finally {
    commit('SET_STATE', { key: 'isLoading', value: false });
  }
};

export const deleteOrganization = async ({ commit }, { payload }) => {
  const db = getFirestore();
  const batchUser = writeBatch(db);
  const batchDevice = writeBatch(db);
  commit('SET_STATE', { key: 'isLoading', value: true });
  const devRefList = [];
  try {
    const orgRef = doc(db, 'organizations', payload.id);
    const usersQuery = query(collection(db, 'users'), where('organization', '==', orgRef));
    const orgSnapshot = await getDocs(usersQuery);

    await deleteDoc(orgRef);
    orgSnapshot.docs.forEach((userDoc) => {
      batchUser.delete(userDoc.ref);
      devRefList.push(userDoc.ref);
    });
    await batchUser.commit();

    if (devRefList.length > 0) {
      const devicesQuery = query(collection(db, 'devices'), where('owner', 'in', devRefList));
      const devSnapshot = await getDocs(devicesQuery);
      devSnapshot.docs.forEach((devDoc) => {
        batchDevice.delete(devDoc.ref);
      });
      await batchDevice.commit();
    }
  } catch (error) {
    commit('SET_STATE', { key: 'error', value: error.message });
  } finally {
    commit('SET_STATE', { key: 'isLoading', value: false });
  }
};

export const getCurrentUserOrg = async ({ commit }) => {
  const db = getFirestore();
  const userUid = getAuth().currentUser.uid;
  const snapshotUser = await getDocs(query(collection(db, 'users'), where('__name__', '==', userUid)));
  let userOrganization;
  if (!snapshotUser.empty) {
    const userData = snapshotUser.docs[0].data();
    if (userData.organization) {
      const orgSnap = await getDocs(query(collection(db, 'organizations'), where('__name__', '==', userData.organization.id)));
      if (!orgSnap.empty) {
        userOrganization = { ...orgSnap.docs[0].data() };
      }
    }
  }
  commit('SET_STATE', { key: 'userOrganization', value: userOrganization });
};

export const resetState = ({ commit }) => {
  commit('RESET_STATE');
};
